/**
 * FileUpload440GLControl.js
 * Created by calhosh on 9/16/2021.
 */

import React, {useState, useMemo, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {DocumentFunctions} from '../../../util/documentfunctions';
import {restoreDocumentVersion, uploadDocumentWithDocumentHelper2} from '../../../actions/Task/documenthubactions';
import {showChangeReasonModal} from '../../../actions/Document/fourFortyGlDocumentActions';
import {raiseDochelperError} from '../../../actions/erroractions';
import ChangeReason from '../ChangeReasonModal/ChangeReason';
import {PropTypes} from 'prop-types';
import {labels} from '../../../util/utils';
import {documentShape} from '../CanvasForm/shapes';
import {updateDocument} from '../../../actions/Document/documentactions';
import {
	createDocumentChangeComment,
	updateDocumentChangeComment
} from '../../../actions/Document/documentextensionactions';
import {useAresUrlParameters, useIsAresView} from '../../../util/Ares/customHooks';
import {startUpchunkUpload} from '../../../actions/Upload/uploadaction';
import {documentTypes} from '../../../util/uiconstants';
import {useUserPreferences} from '../../../util/customHooks';
import Utility from '../../../util/utilityfunctions';
import {getTaskById} from '../../../selectors/Ares/selectors';

function FileUpload440GLControl({
	parentName,
	entityType,
	documentType,
	onDocumentEdited,
	entityUid,
	documentsData,
	requireValidations,
	payloadRef,
	relatedTasks,
	document,
	sectionId,
	groupInstructionDrawerEvidence,
	signoffRequirements,
	bodyTypeId,
	showCrashDocumentWarningMessage
}) {
	// TODO replace documentsData with a selector function received from the parent component (form / body related documents)
	const {taskId} = useAresUrlParameters();

	const userPreferences = useUserPreferences();

	const dispatch = useDispatch();

	const engagement = useSelector((store) => store.engagement);

	const fourFortyGlDocuments = useSelector((store) => store.fourFortyGlDocuments);

	const task = useSelector((store) => getTaskById(store, taskId));

	const documentId = useMemo(() => fourFortyGlDocuments?.uploadDocumentId, [fourFortyGlDocuments]);

	const [uploadInProgress, setUploadInProgress] = useState(false);

	let editSavedChanges = false;

	const onModalConfirm = (engId, docId, documentChangeTypeId, documentExtensionId, documentChangeComment) => {
		// New document upload -> trigger upload action.
		if (!documentId && !uploadInProgress) {
			if (payloadRef && Utility.hasDragAndDropUploadObject()) {
				payloadRef.current = {
					documentExtensionId,
					documentChangeTypeId,
					documentChangeComment,
					docId
				};
				return startDragAndDropUpload();
			} else {
				return processDocumentUpload(
					entityType,
					parentName,
					engId,
					engagement.workspaceId,
					documentType,
					documentExtensionId,
					documentChangeTypeId,
					documentChangeComment,
					fourFortyGlDocuments.showChangeReasonModal,
					task?.taskTypeId,
					relatedTasks,
					signoffRequirements,
					bodyTypeId,
					showCrashDocumentWarningMessage
				);
			}
		} else {
			let promise = DocumentFunctions.onConfirmReasonChange(
				engagement.engagementId,
				documentId,
				documentChangeTypeId,
				documentExtensionId,
				documentChangeComment,
				{},
				dispatch,
				updateDocument,
				updateDocumentChangeComment,
				createDocumentChangeComment,
				signoffRequirements
			);

			promise = promise.then(() => {
				dispatch(showChangeReasonModal(false, documentId));
				editSavedChanges = true; // Avoids restore version API call after clicking Ok
			});

			// Update the current document in the store:

			// promise = promise.then(() => {
			// 	dispatch(updateDocumentById(documentId, payload));
			// });

			// Additional logic after the document has been modified
			promise = promise.then(() => {
				onDocumentEdited && onDocumentEdited(document, documentChangeTypeId);
			});

			return promise;
		}
	};

	const onModalClose = useCallback(() => {
		if (documentId) {
			// Document edit. Restore previous version on Cancel
			if (!editSavedChanges) {
				dispatch(restoreDocumentVersion(engagement.engagementId, documentId)).then(() => {
					dispatch(showChangeReasonModal(false, documentId));
				});
			} else {
				dispatch(showChangeReasonModal(false, documentId));
			}
		} else {
			// Document upload. Just close the modal.
			dispatch(showChangeReasonModal(false));
		}
	}, [documentId, editSavedChanges, dispatch, engagement.engagementId]);

	const doc = useMemo(() => {
		let document;

		if (documentsData?.data && documentsData.data.length > 0 && documentId) {
			document = documentsData.data.find((doc) => doc.id.toUpperCase() === documentId.toUpperCase());
		}

		if (!document) {
			// New file upload. Create empty document object (required by ChangeReasonModal)
			document = {
				id: documentId,
				engagementId: engagement.engagementId,
				documentChangeTypeId: 0
			};
		}

		return document;
	}, [documentsData, documentId, engagement]);

	/**
	 * Upload a document using Document Helper
	 * @param {String} entityType - Type of entity the document is uploaded from (see entityTypes in uiconstants for more details).
	 * @param {String} parentName - evidences.
	 * @param {number} engagementId - engagement id.
	 * @param {number} workspaceId - workspace id.
	 * @param {number} documentType - Document type (see DocumentType in uiconstants for more details).
	 * @param {number} documentExtensionId - Extension id.
	 * @param {number} [documentChangeTypeId] - Reason for changing a document (see documentChangeTypes enumeration for more details) (OPTIONAL).
	 * @param {String} [rationaleText] - Descriptive text for the documentChangeTypeId (OPTIONAL).
	 * @param {boolean} [isUploadAfterRRD] - Boolean value indicating whether the document is being uploaded after the report date or not (OPTIONAL).
	 * @param {number} taskTypeId TaskTypeId (OPTIONAL).
	 * @param {Array} relatedTasks related tasks.
	 * @param {Array} signoffRequirements signoff requirements.
	 * @param {number} bodyTypeId - BodyTypeId (OPTIONAL).
	 * @param {boolean} showCrashDocumentWarningMessage - Show crash document warning message.
	 * @returns {object} Promise with and upload document action.
	 */
	const processDocumentUpload = (
		entityType,
		parentName,
		engagementId,
		workspaceId,
		documentType,
		documentExtensionId,
		documentChangeTypeId,
		rationaleText,
		isUploadAfterRRD,
		taskTypeId,
		relatedTasks,
		signoffRequirements,
		bodyTypeId,
		showCrashDocumentWarningMessage
	) => {
		try {
			setUploadInProgress(true);

			const promise = dispatch(
				uploadDocumentWithDocumentHelper2(
					entityType,
					parentName,
					engagementId,
					workspaceId,
					taskId,
					documentType,
					documentExtensionId,
					documentChangeTypeId,
					rationaleText,
					isUploadAfterRRD,
					taskTypeId,
					{
						connectionid: window?.jQuery?.connection?.documentHubV2?.connection?.id
					},
					fourFortyGlDocuments.entityUid,
					requireValidations,
					sectionId,
					relatedTasks,
					showCrashDocumentWarningMessage,
					signoffRequirements,
					bodyTypeId
				)
			);

			if (promise instanceof Promise) {
				promise.then(function() {
					setUploadInProgress(false);
					onModalClose();
				});

				promise.catch(function() {
					setUploadInProgress(false);
				});
			}

			return promise;
		} catch (e) {
			setUploadInProgress(false);

			dispatch(raiseDochelperError(['0010', 'Connection Failed', labels.documentHelperConnectionIssue]));
		}
	};

	const startDragAndDropUpload = () => {
		try {
			const uploadObject = Utility.getDragAndDropUploadObject();
			if (uploadObject) {
				uploadObject.map((upload) => {
					setUploadInProgress(true);

					const promise = dispatch(
						startUpchunkUpload(
							userPreferences.id, // userId
							engagement.engagementId, // engagementId
							parentName, // parentName
							entityUid, // entityUid
							upload.document?.payload?.id, // documentId
							upload.blob, // document blob
							documentType, // document type id
							17, // document source id, default to doc helper
							upload.overwrite, // overwrite flag
							upload.discard, // discard flag
							true,
							sectionId,
							upload.handleOnDuplicate, // callback
							upload.handleOnComplete, // callback
							upload.handleOnOverwrite, // callback
							relatedTasks,
							showCrashDocumentWarningMessage,
							requireValidations,
							signoffRequirements,
							bodyTypeId
						)
					);

					if (promise instanceof Promise) {
						promise.then(function() {
							setUploadInProgress(false);
							onModalClose();
						});

						promise.catch(function() {
							setUploadInProgress(false);
						});
					}
					return promise;
				});
				Utility.removeDragAndDropUploadObject();
			}
		} catch (e) {
			setUploadInProgress(false);

			dispatch(raiseDochelperError(['0010', 'Connection Failed', labels.documentHelperConnectionIssue]));
		}
	};

	const showModal = useMemo(() => {
		if (!groupInstructionDrawerEvidence) {
			if (fourFortyGlDocuments?.showChangeReasonModal && entityUid === fourFortyGlDocuments.entityUid) {
				return true;
			}
		} else {
			if (fourFortyGlDocuments?.showChangeReasonModal) return true;
		}

		return false;
	}, [
		entityUid,
		fourFortyGlDocuments.entityUid,
		fourFortyGlDocuments?.showChangeReasonModal,
		groupInstructionDrawerEvidence
	]);

	return (
		<ChangeReason
			canvasDocument={doc}
			show={showModal}
			discardChangesLabel={labels.discardChangesLabel}
			modalSize="small"
			onHide={onModalClose}
			onConfirm={onModalConfirm}
		/>
	);
}

//#region Prop-types declarations
FileUpload440GLControl.propTypes = {
	parentName: PropTypes.string,
	entityType: PropTypes.string,
	relatedEntityId: PropTypes.string,
	documentType: PropTypes.number,
	onDocumentEdited: PropTypes.func,
	entityUid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	documentsData: PropTypes.oneOfType([
		PropTypes.shape({
			data: PropTypes.arrayOf(documentShape)
		}),
		PropTypes.arrayOf(documentShape)
	]),
	groupInstructionDrawerEvidence: PropTypes.bool
};

FileUpload440GLControl.defaultProps = {
	groupInstructionDrawerEvidence: false
};
//#endregion

export default FileUpload440GLControl;

// const StyledFileUpload440GLControl = styled.section``;
